import { useEffect, useRef, useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
  RsvpNewPayload,
  UserUpdatePayload,
  SubscriptionChangedPayload,
} from "@prezntz/shared/socket-events";

const SOCKET_URL = import.meta.env.VITE_API_URL || window.location.origin;

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// ============================================
// Connection state management
// ============================================

type ConnectionStatus = "connected" | "connecting" | "disconnected";

let connectionStatus: ConnectionStatus = "disconnected";
const statusListeners = new Set<() => void>();

function setConnectionStatus(status: ConnectionStatus) {
  if (connectionStatus !== status) {
    connectionStatus = status;
    statusListeners.forEach((l) => l());
  }
}

let socket: TypedSocket | null = null;

function getSocket(): TypedSocket {
  if (!socket) {
    const token = localStorage.getItem("access_token");
    socket = io(SOCKET_URL, {
      path: "/ws",
      transports: ["websocket", "polling"],
      autoConnect: true,
      auth: token ? { token } : undefined,
      // Reconnection with exponential backoff + jitter
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,       // start at 1s
      reconnectionDelayMax: 30000,   // cap at 30s
      randomizationFactor: 0.5,      // ±50% jitter on each delay
    }) as TypedSocket;

    // Track connection state changes
    socket.on("connect", () => {
      setConnectionStatus("connected");
    });

    socket.io.on("reconnect_attempt", () => {
      setConnectionStatus("connecting");
    });

    socket.on("disconnect", () => {
      setConnectionStatus("disconnected");
    });

    socket.io.on("reconnect", () => {
      setConnectionStatus("connected");
      // Re-authenticate after reconnect by re-sending auth token
      const freshToken = localStorage.getItem("access_token");
      if (freshToken && socket) {
        socket.auth = { token: freshToken };
      }
    });

    socket.io.on("reconnect_failed", () => {
      setConnectionStatus("disconnected");
    });
  }
  return socket;
}

/** Reconnect with fresh auth token (call after login/token refresh) */
export function reconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  setConnectionStatus("disconnected");
  // Lazy-reconnect on next getSocket() call
}

// Re-export payload types for consumer convenience
export type { RsvpNewPayload, UserUpdatePayload, SubscriptionChangedPayload };
// Alias for backward compatibility
export type RsvpEvent = RsvpNewPayload;

/**
 * Get the current WebSocket connection status.
 * Uses useSyncExternalStore for tear-free reads.
 */
export function useSocketStatus(): ConnectionStatus {
  return useSyncExternalStore(
    (cb) => {
      statusListeners.add(cb);
      return () => statusListeners.delete(cb);
    },
    () => connectionStatus
  );
}

/**
 * Subscribe to real-time RSVP updates for a card.
 * Automatically joins/leaves the card room.
 */
export function useCardRsvpSocket(
  cardId: string | undefined,
  onRsvp: (data: RsvpNewPayload) => void
) {
  const callbackRef = useRef(onRsvp);
  callbackRef.current = onRsvp;

  const stableCallback = useCallback((data: RsvpNewPayload) => {
    callbackRef.current(data);
  }, []);

  useEffect(() => {
    if (!cardId) return;

    const s = getSocket();
    s.emit("join:card", cardId);
    s.on("rsvp:new", stableCallback);

    // Re-join room after reconnection
    const onReconnect = () => {
      s.emit("join:card", cardId);
    };
    s.io.on("reconnect", onReconnect);

    return () => {
      s.emit("leave:card", cardId);
      s.off("rsvp:new", stableCallback);
      s.io.off("reconnect", onReconnect);
    };
  }, [cardId, stableCallback]);
}

/**
 * Subscribe to real-time user profile updates.
 * Server pushes updates when profile, preferences, or admin status changes.
 */
export function useUserUpdateSocket(
  onUpdate: (data: UserUpdatePayload) => void
) {
  const callbackRef = useRef(onUpdate);
  callbackRef.current = onUpdate;

  const stableCallback = useCallback((data: UserUpdatePayload) => {
    callbackRef.current(data);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    const s = getSocket();
    s.on("user:updated", stableCallback);

    return () => {
      s.off("user:updated", stableCallback);
    };
  }, [stableCallback]);
}

/**
 * Subscribe to real-time contact sync events.
 * Invalidates the contacts query when any contact is created, updated, or deleted
 * on another tab or device in the same user session.
 */
export function useContactSyncSocket() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    const s = getSocket();

    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    };

    s.on("contact:updated", handler);

    return () => {
      s.off("contact:updated", handler);
    };
  }, [queryClient]);
}

/**
 * Subscribe to real-time subscription changes.
 * Server pushes updates when Stripe webhooks trigger tier/status changes.
 */
export function useSubscriptionSocket(
  onChanged: (data: SubscriptionChangedPayload) => void
) {
  const callbackRef = useRef(onChanged);
  callbackRef.current = onChanged;

  const stableCallback = useCallback((data: SubscriptionChangedPayload) => {
    callbackRef.current(data);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;

    const s = getSocket();
    s.on("user:subscription_changed", stableCallback);

    return () => {
      s.off("user:subscription_changed", stableCallback);
    };
  }, [stableCallback]);
}

/**
 * Listen for push notification click messages from the service worker.
 * When the app is already open, the SW posts a message instead of opening a new tab.
 * This hook receives those messages and navigates the app to the target URL.
 */
export function useNotificationNavigation() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === "notification_click" && event.data?.url) {
        navigate(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);
}
